<script lang="ts">
	import SvelteSeo from 'svelte-seo';
	import { page } from '$app/stores';
	import { onMount } from 'svelte';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import { Textarea } from '$lib/components/ui/textarea';
	import { Alert, AlertDescription } from '$lib/components/ui/alert';
	import { Card, CardContent, CardHeader } from '$lib/components/ui/card';
	import { Badge } from '$lib/components/ui/badge';
	import * as Tabs from '$lib/components/ui/tabs';
	import {
		CheckCircle,
		Sparkles,
		CloudUpload,
		FileText,
		TrendingUp,
		Zap,
		Loader2,
		X
	} from '@lucide/svelte';
	import { createPageSEO, createWebPageSchema } from '$lib/config/seo';
	import { useTest } from '$lib/components/tracking/useTest';
	import { cvUploadTest } from '$lib/components/tracking/ab-tests';
	import { fetchSSE } from '$lib/api';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';
	import { STAGE_LABELS } from '$lib/config/consts';
	import { PUBLIC_DASHBOARD_URL } from '$env/static/public';
	import { isDashboardAuthenticated } from '$lib/utils/auth-cookie';

	interface JDData {
		company_name: string | null;
		company_website: string | null;
		company_email: string | null;
		job_title: string | null;
		location: string | null;
		remote_type: 'remote' | 'hybrid' | 'onsite' | null;
		employment_type: 'full-time' | 'part-time' | 'contract' | null;
		description: string | null;
		requirements: string[];
		responsibilities: string[];
		salary_range: string | null;
		experience_required: string | null;
		skills_required: string[];
	}

	interface DropSSEResult {
		success: boolean;
		type: 'cv' | 'jd' | 'unknown';
		saved: boolean;
		id?: string;
		token?: string;
		data?: JDData;
		missing_fields?: string[];
		confidence?: number;
		error?: string;
	}

	const JD_FIELD_LABELS: Record<string, string> = {
		company_name: 'Company',
		company_email: 'Company Email',
		company_website: 'Website',
		job_title: 'Job Title',
		location: 'Location',
		description: 'Description',
		skills_required: 'Required Skills',
		requirements: 'Requirements',
		responsibilities: 'Responsibilities',
		salary_range: 'Salary',
		employment_type: 'Employment Type',
		remote_type: 'Remote Policy',
		experience_required: 'Experience'
	};

	function format_field_value(value: unknown): string {
		if (value === null || value === undefined) return '';
		if (typeof value === 'string') return value;
		if (Array.isArray(value)) {
			if (value.length === 0) return '';
			if (typeof value[0] === 'string') return value.join(', ');
			return JSON.stringify(value);
		}
		return String(value);
	}

	const test = useTest(cvUploadTest);
	const APP_URL = PUBLIC_DASHBOARD_URL || 'https://app.talentprofile.net';

	type Step = 'idle' | 'uploading' | 'parsed' | 'submitting' | 'success' | 'error';
	let step = $state<Step>('idle');
	let error_message = $state('');
	let progress_stage = $state('');
	let progress_completed = $state(0);
	let progress_total = $state(0);
	let parsed_data = $state<JDData | null>(null);
	let jd_file = $state<File | null>(null);
	let jd_text = $state('');
	let partial_fields = $state<Record<string, unknown>>({});
	let is_app_user = $state(false);

	let company_name = $state('');
	let company_email = $state('');
	let company_website = $state('');
	let job_title = $state('');
	let job_description = $state('');
	let job_location = $state('');
	let salary_range = $state('');
	let job_url = $state('');
	let employment_type = $state('');
	let remote_type = $state('');
	let experience_level = $state('');
	let required_skills = $state('');
	let requirements_text = $state('');
	let responsibilities_text = $state('');
	let contact_name = $state('');
	let contact_email = $state('');

	const has_partial_data = $derived(Object.keys(partial_fields).length > 0);

	const partial_entries = $derived(
		Object.entries(partial_fields).filter(([, value]) => format_field_value(value) !== '')
	);

	const pending_keys = $derived(
		Object.keys(JD_FIELD_LABELS)
			.filter((k) => !(k in partial_fields))
			.slice(0, 3)
	);

	onMount(() => {
		is_app_user = isDashboardAuthenticated();
	});

	function populate_form(data: JDData) {
		company_name = data.company_name || '';
		company_email = data.company_email || '';
		company_website = data.company_website || '';
		job_title = data.job_title || '';
		job_description = data.description || '';
		job_location = data.location || '';
		salary_range = data.salary_range || '';
		employment_type = data.employment_type || '';
		remote_type = data.remote_type || '';
		experience_level = data.experience_required || '';
		required_skills = data.skills_required?.join(', ') || '';
		requirements_text = data.requirements?.join('\n') || '';
		responsibilities_text = data.responsibilities?.join('\n') || '';
	}

	const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

	async function handle_jd_upload() {
		if (!jd_file && !jd_text) return;

		test.event('file-selected', jd_file?.size ?? jd_text.length);
		step = 'uploading';
		progress_stage = 'uploading';
		progress_completed = 0;
		progress_total = 4;
		error_message = '';
		partial_fields = {};
		test.event('upload-started');

		try {
			let result: DropSSEResult;

			if (jd_file) {
				const form_data = new FormData();
				form_data.append('file', jd_file);
				form_data.append('ab_test', test.id);
				form_data.append('ab_variant', test.variant);

				result = await fetchSSE<DropSSEResult>(
					{
						url: '/drop',
						method: 'POST',
						formData: form_data
					},
					{
						onProgress: (event) => {
							progress_stage = event.stage;
							if (event.completed !== undefined) progress_completed = event.completed;
							if (event.total !== undefined) progress_total = event.total;
						},
						onField: (key, value) => {
							partial_fields = { ...partial_fields, [key]: value };
						},
						onFieldReset: () => {
							partial_fields = {};
						}
					}
				);
			} else {
				result = await fetchSSE<DropSSEResult>(
					{
						url: '/drop/text',
						method: 'POST',
						json: { text: jd_text, hint: 'jd' }
					},
					{
						onProgress: (event) => {
							progress_stage = event.stage;
							if (event.completed !== undefined) progress_completed = event.completed;
							if (event.total !== undefined) progress_total = event.total;
						},
						onField: (key, value) => {
							partial_fields = { ...partial_fields, [key]: value };
						},
						onFieldReset: () => {
							partial_fields = {};
						}
					}
				);
			}

			if (!result.success) {
				throw new Error(result.error || 'Failed to parse job description');
			}

			test.event('upload-completed');

			if (result.type !== 'jd') {
				step = 'error';
				error_message =
					'This document was not recognized as a Job Description. Please try uploading a JD.';
				test.event('upload-failed');
				return;
			}

			test.event('parse-completed');

			if (result.saved && result.id) {
				step = 'success';
				test.event('record-saved');
				test.goal(1);
				return;
			}

			parsed_data = (result.data as JDData) || ({} as JDData);
			populate_form(parsed_data);
			step = 'parsed';
			test.event('form-viewed');
		} catch (e) {
			step = 'error';
			error_message = e instanceof Error ? e.message : 'Failed to parse job description';
			test.event('upload-failed');
		}
	}

	function skip_to_manual() {
		parsed_data = {} as JDData;
		step = 'parsed';
	}

	async function handle_submit() {
		error_message = '';

		if (!company_name || !contact_email || !job_title || !job_description || !job_location) {
			error_message = 'Please fill in all required fields';
			return;
		}

		if (!EMAIL_REGEX.test(contact_email)) {
			error_message = 'Please enter a valid email address';
			return;
		}

		step = 'submitting';
		progress_stage = 'saving';

		try {
			const submit_data: JDData & { contact_name?: string; job_url?: string } = {
				company_name,
				company_website: company_website || null,
				company_email: contact_email || company_email || null,
				job_title,
				location: job_location,
				remote_type: (remote_type as JDData['remote_type']) || null,
				employment_type: (employment_type as JDData['employment_type']) || null,
				description: job_description,
				requirements: requirements_text
					? requirements_text
							.split('\n')
							.map((s) => s.trim())
							.filter(Boolean)
					: parsed_data?.requirements || [],
				responsibilities: responsibilities_text
					? responsibilities_text
							.split('\n')
							.map((s) => s.trim())
							.filter(Boolean)
					: parsed_data?.responsibilities || [],
				salary_range: salary_range || null,
				experience_required: experience_level || null,
				skills_required: required_skills
					? required_skills
							.split(',')
							.map((s) => s.trim())
							.filter(Boolean)
					: [],
				contact_name: contact_name || undefined,
				job_url: job_url || undefined
			};

			const result = await fetchSSE<{ success: boolean; id?: string; error?: string }>(
				{
					url: '/drop/complete',
					method: 'POST',
					json: {
						type: 'jd',
						data: submit_data,
						ab_test: test.id,
						ab_variant: test.variant
					}
				},
				{
					onProgress: (event) => {
						progress_stage = event.stage;
					}
				}
			);

			if (!result.success) {
				throw new Error(result.error || 'Failed to save');
			}

			test.event('record-saved');
			test.goal(1);
			step = 'success';
		} catch (e) {
			step = 'parsed';
			error_message = e instanceof Error ? e.message : 'Failed to save';
		}
	}

	function reset() {
		step = 'idle';
		error_message = '';
		progress_stage = '';
		progress_completed = 0;
		progress_total = 0;
		jd_file = null;
		jd_text = '';
		partial_fields = {};
		parsed_data = null;
		company_name = '';
		company_email = '';
		company_website = '';
		job_title = '';
		job_description = '';
		job_location = '';
		salary_range = '';
		job_url = '';
		employment_type = '';
		remote_type = '';
		experience_level = '';
		required_skills = '';
		requirements_text = '';
		responsibilities_text = '';
		contact_name = '';
		contact_email = '';
	}

	const seo = createPageSEO({
		title: 'Post Your Job Description - Find Developers in 24h | TalentProfile',
		description:
			'Drop your job description and get matched with qualified developers within 24 hours. AI-powered extraction means zero form filling. 28K+ pre-screened developers. Free forever.',
		canonical: '/drop-jd',
		keywords:
			'post job description, hire developers fast, AI job posting, tech recruitment, developer hiring',
		ogImage: '/drop-jd.png',
		ogType: 'website',
		structuredData: createWebPageSchema({
			name: 'Post Job Description',
			description: 'AI-powered job posting for hiring developers',
			url: '/drop-jd'
		})
	});
</script>

{#key $page.url.pathname}
	<SvelteSeo {...seo} />
{/key}
<div class="flex min-h-[calc(100dvh-527px)] flex-col gap-4 bg-gradient-to-b from-white to-gray-50">
	<div class="container mx-auto px-4 py-16 md:py-20">
		{#if step === 'idle'}
			<div class="mx-auto max-w-7xl">
				<div class="grid items-center gap-12 sm:gap-24 md:gap-36 lg:grid-cols-2">
					<div class="space-y-6">
						<Badge class="mb-2" variant="secondary">
							<Sparkles class="mr-1 h-4 w-4" />
							100% Free • AI-Powered • Zero Forms
						</Badge>

						<h1 class="text-4xl leading-tight font-bold text-gray-900 md:text-6xl">
							Drop Your JD,<br />
							<span class="text-blue-600">Get Developers</span><br />
							in 24 Hours
						</h1>

						<p class="text-xl leading-relaxed text-gray-600">
							No more filling 20+ form fields. Just drop your job description and our AI extracts
							everything. We'll match you with qualified developers immediately.
						</p>

						<div class="grid grid-cols-3 gap-6 py-4">
							<div>
								<div class="text-3xl font-bold text-blue-600">28K+</div>
								<div class="text-sm text-gray-600">Developers</div>
							</div>
							<div>
								<div class="text-3xl font-bold text-blue-600">24h</div>
								<div class="text-sm text-gray-600">First matches</div>
							</div>
							<div>
								<div class="text-3xl font-bold text-blue-600">92%</div>
								<div class="text-sm text-gray-600">Match rate</div>
							</div>
						</div>

						<div class="flex flex-wrap items-center gap-6 pt-4 text-sm text-gray-600">
							<div class="flex items-center gap-2">
								<CheckCircle class="h-5 w-5 text-green-600" />
								<span>Zero form fields</span>
							</div>
							<div class="flex items-center gap-2">
								<CheckCircle class="h-5 w-5 text-green-600" />
								<span>AI extracts everything</span>
							</div>
							<div class="flex items-center gap-2">
								<CheckCircle class="h-5 w-5 text-green-600" />
								<span>Instant matching</span>
							</div>
						</div>

						<div class="space-y-3 pt-6 text-sm text-gray-600">
							<div class="flex items-start gap-3">
								<Zap class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
								<div>
									<strong class="text-gray-900">AI Understands Context:</strong> "5+ years React" becomes
									requirements automatically. No manual field filling.
								</div>
							</div>
							<div class="flex items-start gap-3">
								<Zap class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
								<div>
									<strong class="text-gray-900">Instant Matching:</strong> We start matching against 28,000+
									developers the moment you submit.
								</div>
							</div>
							<div class="flex items-start gap-3">
								<Zap class="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
								<div>
									<strong class="text-gray-900">Zero Maintenance:</strong> Drop it and forget it. We handle
									everything else.
								</div>
							</div>
						</div>
					</div>

					<div class="relative">
						<Card class="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
							<CardContent class="pt-6">
								<Tabs.Root value="file" class="w-full">
									<Tabs.List class="mb-6 grid w-full grid-cols-2">
										<Tabs.Trigger value="file">
											<FileText class="mr-2 h-4 w-4" />
											Upload File
										</Tabs.Trigger>
										<Tabs.Trigger value="paste">
											<FileText class="mr-2 h-4 w-4" />
											Paste Text
										</Tabs.Trigger>
									</Tabs.List>

									<Tabs.Content value="file" class="space-y-6">
										<div class="space-y-6 py-8 text-center">
											<div
												class="superellipse mx-auto flex h-20 w-20 animate-pulse items-center justify-center bg-blue-100"
											>
												<CloudUpload class="h-10 w-10 text-blue-600" />
											</div>

											<div>
												<h2 class="mb-2 text-2xl font-bold text-gray-900">Drop Your JD Here</h2>
												<p class="text-gray-600">PDF, DOC, DOCX, or TXT • Max 10MB</p>
											</div>

											<Input
												id="jd_file"
												type="file"
												accept=".pdf,.doc,.docx,.txt"
												class="cursor-pointer"
												onchange={(e) => {
													const target = e.target as HTMLInputElement;
													jd_file = target.files?.[0] || null;
												}}
											/>

											<Button
												size="lg"
												class="w-full"
												onclick={handle_jd_upload}
												disabled={!jd_file}
											>
												<Sparkles class="mr-2 h-5 w-5" />
												Extract with AI
											</Button>

											<div class="space-y-2 border-t pt-4 text-left text-sm text-gray-600">
												<div class="flex items-start gap-2">
													<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
													<span>AI extracts job title, requirements, skills automatically</span>
												</div>
												<div class="flex items-start gap-2">
													<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
													<span>Instant matching against 28K+ developers</span>
												</div>
												<div class="flex items-start gap-2">
													<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
													<span>Get first candidates within 24 hours</span>
												</div>
											</div>

											<div class="border-t pt-4">
												<div class="flex items-center justify-center gap-2 text-sm text-gray-600">
													<TrendingUp class="h-4 w-4 text-green-600" />
													<span>Used by 500+ companies hiring developers</span>
												</div>
											</div>
										</div>
									</Tabs.Content>

									<Tabs.Content value="paste" class="space-y-6">
										<div class="space-y-4">
											<Label for="jd_text" class="text-base font-semibold"
												>Paste Your Job Description</Label
											>
											<Textarea
												id="jd_text"
												bind:value={jd_text}
												placeholder="Paste your complete job description here...&#10;&#10;Example:&#10;Senior Backend Engineer&#10;&#10;We're looking for an experienced backend engineer with 5+ years of Node.js experience..."
												rows={12}
												class="resize-none bg-white"
											/>

											<Button
												size="lg"
												class="w-full"
												onclick={handle_jd_upload}
												disabled={!jd_text}
											>
												<Sparkles class="mr-2 h-5 w-5" />
												Extract with AI
											</Button>

											<div class="space-y-2 border-t pt-4 text-sm text-gray-600">
												<div class="flex items-start gap-2">
													<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
													<span>AI extracts everything automatically</span>
												</div>
												<div class="flex items-start gap-2">
													<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
													<span>No manual form filling required</span>
												</div>
												<div class="flex items-start gap-2">
													<CheckCircle class="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />
													<span>Instant matching starts immediately</span>
												</div>
											</div>
										</div>
									</Tabs.Content>
								</Tabs.Root>

								<div class="mt-6 border-t pt-4 text-center">
									<p class="text-sm text-gray-600">
										Or <button
											type="button"
											class="font-semibold text-blue-600 hover:underline"
											onclick={skip_to_manual}>skip and fill manually</button
										> (takes 10x longer)
									</p>
								</div>
							</CardContent>
						</Card>

						<div
							class="absolute -top-4 -right-4 animate-bounce rounded-full bg-green-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
						>
							100% Free
						</div>
						<div
							class="absolute -bottom-4 -left-4 rounded-full bg-purple-500 px-4 py-2 text-sm font-semibold text-white shadow-lg"
						>
							Zero Forms
						</div>
					</div>
				</div>
			</div>
		{:else if step === 'uploading' || step === 'submitting'}
			<div class="mx-auto max-w-md">
				<Card>
					<CardContent class="flex flex-col items-center py-8">
						{#if !has_partial_data}
							<Loader2 class="mb-4 h-12 w-12 animate-spin text-blue-600" />
						{/if}
						<p class="text-lg font-medium text-gray-900">
							{STAGE_LABELS[progress_stage] || 'Processing...'}
						</p>
						{#if progress_total > 0 && !has_partial_data}
							<div class="mt-6 w-full max-w-xs">
								<div class="h-2 w-full overflow-hidden rounded-full bg-gray-200">
									<div
										class="h-full rounded-full bg-blue-600 transition-all duration-500"
										style="width: {Math.min(
											100,
											Math.max(0, Math.round((progress_completed / progress_total) * 100))
										)}%"
									></div>
								</div>
								<p class="mt-2 text-center text-xs text-gray-500">
									Step {progress_completed} of {progress_total}
								</p>
							</div>
						{/if}

						{#if has_partial_data}
							<div class="mt-6 w-full space-y-2">
								{#each partial_entries as [key, value] (key)}
									<div
										class="animate-in fade-in slide-in-from-left-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2"
									>
										<p class="text-xs font-medium text-gray-500">
											{JD_FIELD_LABELS[key] || key}
										</p>
										<p class="line-clamp-2 text-sm text-gray-900">
											{format_field_value(value)}
										</p>
									</div>
								{/each}

								{#each pending_keys as pendingKey (pendingKey)}
									<div class="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
										<div class="h-3 w-16 animate-pulse rounded bg-gray-200"></div>
										<div class="mt-1 h-4 w-3/4 animate-pulse rounded bg-gray-200"></div>
									</div>
								{/each}
							</div>
						{/if}
					</CardContent>
				</Card>
			</div>
		{:else if step === 'parsed'}
			<div class="mx-auto max-w-4xl space-y-8">
				<Alert class="border-green-200 bg-green-50">
					<AlertDescription class="flex items-center gap-2 text-green-800">
						<CheckCircle class="h-5 w-5" />
						{parsed_data?.job_title
							? 'JD parsed! Review the extracted details below and submit.'
							: 'Fill in your job details below.'}
					</AlertDescription>
				</Alert>

				{#if error_message}
					<Alert class="border-red-200 bg-red-50">
						<AlertDescription class="text-red-800">{error_message}</AlertDescription>
					</Alert>
				{/if}

				<h2 class="text-3xl font-bold text-gray-900">Review Your Job Details</h2>

				<div class="space-y-6">
					<Card class="shadow-none">
						<CardHeader>
							<h3 class="text-xl font-semibold">Company Information</h3>
						</CardHeader>
						<CardContent class="space-y-4">
							<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
								<div class="space-y-2">
									<Label for="company_name">Company Name *</Label>
									<Input id="company_name" bind:value={company_name} required />
								</div>
								<div class="space-y-2">
									<Label for="company_email">Company Email</Label>
									<Input id="company_email" type="email" bind:value={company_email} />
								</div>
							</div>
							<div class="space-y-2">
								<Label for="company_website">Company Website</Label>
								<Input id="company_website" type="url" bind:value={company_website} />
							</div>
						</CardContent>
					</Card>

					<Card class="shadow-none">
						<CardHeader>
							<h3 class="text-xl font-semibold">Job Details</h3>
						</CardHeader>
						<CardContent class="space-y-4">
							<div class="space-y-2">
								<Label for="job_title">Job Title *</Label>
								<Input id="job_title" bind:value={job_title} required />
							</div>
							<div class="space-y-2">
								<Label for="job_description">Job Description *</Label>
								<Textarea id="job_description" bind:value={job_description} rows={8} required />
							</div>
							<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
								<div class="space-y-2">
									<Label for="job_location">Location *</Label>
									<Input id="job_location" bind:value={job_location} required />
								</div>
								<div class="space-y-2">
									<Label for="salary_range">Salary Range</Label>
									<Input
										id="salary_range"
										bind:value={salary_range}
										placeholder="e.g. $120k-$180k"
									/>
								</div>
							</div>
							<div class="space-y-2">
								<Label for="job_url">Application URL</Label>
								<Input
									id="job_url"
									type="url"
									bind:value={job_url}
									placeholder="https://your-company.com/careers/job-id"
								/>
							</div>
						</CardContent>
					</Card>

					<Card class="shadow-none">
						<CardHeader>
							<h3 class="text-xl font-semibold">Requirements</h3>
						</CardHeader>
						<CardContent class="space-y-4">
							<div class="grid grid-cols-1 gap-4 md:grid-cols-3">
								<div class="space-y-2">
									<Label for="employment_type">Employment Type</Label>
									<select
										id="employment_type"
										bind:value={employment_type}
										class="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
									>
										<option value="">Select type</option>
										<option value="full-time">Full-time</option>
										<option value="part-time">Part-time</option>
										<option value="contract">Contract</option>
									</select>
								</div>
								<div class="space-y-2">
									<Label for="remote_type">Remote Policy</Label>
									<select
										id="remote_type"
										bind:value={remote_type}
										class="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
									>
										<option value="">Select policy</option>
										<option value="remote">Fully Remote</option>
										<option value="hybrid">Hybrid</option>
										<option value="onsite">On-site</option>
									</select>
								</div>
								<div class="space-y-2">
									<Label for="experience_level">Experience Level</Label>
									<select
										id="experience_level"
										bind:value={experience_level}
										class="border-input bg-background flex h-10 w-full rounded-md border px-3 py-2 text-sm"
									>
										<option value="">Select level</option>
										<option value="entry">Entry Level</option>
										<option value="mid">Mid Level</option>
										<option value="senior">Senior</option>
										<option value="lead">Lead/Principal</option>
									</select>
								</div>
							</div>
							<div class="space-y-2">
								<Label for="required_skills"
									>Required Skills <span class="text-xs text-gray-500">(comma-separated)</span
									></Label
								>
								<Input
									id="required_skills"
									bind:value={required_skills}
									placeholder="Node.js, React, PostgreSQL, AWS"
								/>
							</div>
							<div class="space-y-2">
								<Label for="requirements_text"
									>Requirements <span class="text-xs text-gray-500">(one per line)</span></Label
								>
								<Textarea
									id="requirements_text"
									bind:value={requirements_text}
									rows={4}
									placeholder="5+ years of backend development&#10;Experience with distributed systems&#10;Strong problem-solving skills"
								/>
							</div>
							<div class="space-y-2">
								<Label for="responsibilities_text"
									>Responsibilities <span class="text-xs text-gray-500">(one per line)</span></Label
								>
								<Textarea
									id="responsibilities_text"
									bind:value={responsibilities_text}
									rows={4}
									placeholder="Design and implement backend services&#10;Mentor junior engineers&#10;Participate in code reviews"
								/>
							</div>
						</CardContent>
					</Card>

					<Card class="shadow-none">
						<CardHeader>
							<h3 class="text-xl font-semibold">Your Contact Information</h3>
						</CardHeader>
						<CardContent class="space-y-4">
							<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
								<div class="space-y-2">
									<Label for="contact_name">Your Name</Label>
									<Input id="contact_name" bind:value={contact_name} />
								</div>
								<div class="space-y-2">
									<Label for="contact_email">Your Email *</Label>
									<Input id="contact_email" type="email" bind:value={contact_email} required />
								</div>
							</div>
						</CardContent>
					</Card>

					<Card class="border-blue-200 bg-blue-50">
						<CardContent class="space-y-4 pt-6">
							<Button class="w-full" size="lg" onclick={handle_submit}>
								<CheckCircle class="mr-2 h-5 w-5" />
								Submit & Start Matching
							</Button>

							<p class="text-center text-xs text-gray-600">
								By submitting, you agree to our <a
									href="/terms"
									class="text-blue-600 hover:underline">Terms of Service</a
								>
							</p>
						</CardContent>
					</Card>
				</div>
			</div>
		{:else if step === 'success'}
			<div class="mx-auto max-w-md">
				<Card class="border-green-200 bg-green-50">
					<CardContent class="flex flex-col items-center py-12 text-center">
						<div class="mb-4 rounded-full bg-green-100 p-3">
							<CheckCircle class="h-10 w-10 text-green-600" />
						</div>
						<h2 class="mb-2 text-2xl font-bold text-gray-900">Job Posted!</h2>
						<p class="text-gray-600">We'll start matching you with qualified developers.</p>

						{#if is_app_user}
							<div class="mt-6 w-full space-y-3 border-t pt-4">
								<p class="text-sm text-gray-500">
									You already have an account. Your job listing will be linked automatically on your
									next sign-in.
								</p>
								<Button
									variant="outline"
									class="w-full"
									onclick={() => window.open(`${APP_URL}/company/dashboard?claim_drop=1`, '_blank')}
								>
									Go to Dashboard
								</Button>
							</div>
						{/if}
					</CardContent>
				</Card>
			</div>
		{:else if step === 'error'}
			<div class="mx-auto max-w-md">
				<Card class="border-red-200 bg-red-50">
					<CardContent class="flex flex-col items-center py-12 text-center">
						<div class="mb-4 rounded-full bg-red-100 p-3">
							<X class="h-10 w-10 text-red-600" />
						</div>
						<h2 class="mb-2 text-2xl font-bold text-gray-900">Something went wrong</h2>
						<p class="mb-4 text-gray-600">{error_message}</p>
						<Button variant="outline" onclick={reset}>Try Again</Button>
					</CardContent>
				</Card>
			</div>
		{/if}
	</div>

	{#if step === 'success'}
		<div class="container mx-auto px-4 py-8 md:pb-24">
			<GlopayCtaHorizontal placement="drop-jd-post-form" />
		</div>
	{/if}
</div>
